import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import Button from '@components/dataEntry/button/Button';
import TextInput from '@components/dataEntry/Input/TextInput';
import PasswordInput from '@components/dataEntry/Input/PasswordInput/index';
import { handleEnterKeyPress } from '@utils/handleKeypress.function';
import { validateEmailInput } from '@utils/loginValidate.function';
import { useNavigate } from 'react-router-dom';
import {
  loginAndAddAccount,
  loginWithEmail,
} from '@store/firebase/user/registerUser';
import ErrorModal from './ErrorModal';
import ErrorTypes from '@pages/Register/constants/errorTypes';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  currentUserAuthTypeState,
  currentUserIDState,
} from '@atom/setting/user';
import { fuego, useDocument } from '@nandorojo/swr-firestore';
import { RootPath } from '@routes/routePath';
import { User } from '@typings/setting/userType.interface';
import {
  CollectionPath,
  SettingCollectionPath,
} from '@typings/collections.enum';
import { gatePageActiveState } from '@atom/setting/homepageSet';
import ResetPasswordModal from './ResetPassword/ResetPasswordModal';
import { IhomepageSetting } from '@typings/setting/homepageSetting.interface';

const Wrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const BtnWrap = styled.div`
  display: flex;
  gap: 16px;
`;

const Info = styled.div`
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 6px;
  background-color: var(--inkColor);
  display: flex;
  justify-content: center;
  align-items: center;
`;

type LoginProps = {
  handleGoBack?: any;
  toggleLogin?: () => void;
  setTitle?: boolean;
  addAccount?: boolean;
  loginText?: string;
};

function Login({
  handleGoBack,
  addAccount,
  toggleLogin,
  setTitle = true,
  loginText,
}: LoginProps) {
  const history = useNavigate();
  const currentUserId = fuego.auth().currentUser?.uid;
  const currentUserEmail = fuego.auth().currentUser?.email || '';
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isPrevPasswordValid, setIsPrevPasswordValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [btnDisabled, setBtnDistabled] = useState(true);
  const [errorType, setErrorType] = useState('');
  const [showError, setShowError] = useState(false);
  const [userUid, setCurrentUid] = useRecoilState(currentUserIDState);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [prevPassword, setPrevPassword] = useState('');
  const setCurrentUserAuthType = useSetRecoilState(currentUserAuthTypeState);
  const setGatePageActive = useSetRecoilState(gatePageActiveState);
  const [openResetPassword, setOpenResetPassword] = useState(false);

  const closeResetPasswordModal = () => setOpenResetPassword(false);

  const resetInput = useCallback(() => {
    setEmail('');
    setPassword('');
  }, []);

  const ErrorTypeMap = {
    [ErrorTypes.NoExistEmail.value]: (
      <ErrorModal
        show={showError}
        handleClose={() => setShowError(!showError)}
        errorType={ErrorTypes.NoExistEmail}
      />
    ),
    [ErrorTypes.WrongEmailPassword.value]: (
      <ErrorModal
        show={showError}
        handleClose={() => setShowError(!showError)}
        errorType={ErrorTypes.WrongEmailPassword}
      />
    ),
  };
  const { data: currentUserData } = useDocument<User>(
    `${CollectionPath.USERS}/${currentUserId}`,
    { listen: true },
  );

  const { data: homeSet, set } = useDocument<IhomepageSetting>(
    SettingCollectionPath.HOMEPAGE,
    { listen: true },
  );

  useEffect(() => {
    const baseValid = !isEmailValid && !isPasswordValid && email && password;
    if (addAccount) {
      setBtnDistabled(!(!isPrevPasswordValid && prevPassword && baseValid));
    } else {
      setBtnDistabled(!baseValid);
    }
  }, [email, password, prevPassword, addAccount, isEmailValid, isPasswordValid, isPrevPasswordValid]);

  useEffect(() => {
    if (currentUserData) {
      setCurrentUserAuthType(currentUserData.authorizationType);
    }
  }, [currentUserData]);

  const changeEmail = useCallback((e: any) => {
    const value: string = e.target.value;
    const isValid: boolean = validateEmailInput(value);

    setEmail(value);
    setIsEmailValid(!isValid);
  }, []);

  const changePassword = useCallback((e: any) => {
    const value: string = e.target.value;
    const isValid: boolean = value.length > 4;
    setPassword(value);
    setIsPasswordValid(!isValid);
  }, []);

  const changePrevPassword = useCallback((e: any) => {
    const value: string = e.target.value;
    const isValid: boolean = value.length > 4;
    setPrevPassword(value);
    setIsPrevPasswordValid(!isValid);
  }, []);

  const handleLogin = useCallback(async () => {
    // reload 대기 중 Enter 재입력 / 더블 클릭으로 인한 중복 signIn 방지 가드
    if (isLoading || loginSuccess) return;
    setIsLoading(true);
    try {
      if (addAccount) {
        await loginAndAddAccount({
          prevUid: currentUserId,
          prevEmail: currentUserEmail,
          prevPassword,
          email,
          password,
        });
      }
      const userToken = await loginWithEmail({ email, password });

      if (!addAccount && userToken) {
        // 대문 페이지 race condition 방지: reload 이전에 입력/버튼을 즉시 잠금
        setLoginSuccess(true);
        location.reload();
        return;
      }

      // addAccount 경로: reload 없이 기존 계정 Recoil 유지
      setCurrentUid(currentUserId);
      setIsLoading(false);
      setGatePageActive(userToken ? false : true);
      resetInput();
      if (!addAccount) {
        handleGoBack?.();
      }
    } catch (e) {
      setBtnDistabled(false);
      setIsLoading(false);
      setShowError(true);
      switch (e.code) {
        case 'auth/invalid-email':
          setErrorType(ErrorTypes.WrongEmailPassword.value);
          break;
        case 'auth/wrong-password':
          setErrorType(ErrorTypes.WrongEmailPassword.value);
          break;
        case 'auth/user-not-found':
          setErrorType(ErrorTypes.NoExistEmail.value);
          break;
        default:
          setErrorType(ErrorTypes.WrongEmailPassword.value);
      }
    }
  }, [currentUserId, email, password, isLoading, loginSuccess, addAccount]);

  return (
    <Wrap>
      {setTitle && <h4>로그인</h4>}
      {addAccount && (
        <PasswordInput
          block
          value={prevPassword}
          onChange={changePrevPassword}
          placeholder={`현재 계정(${currentUserEmail}) 비밀번호`}
          isValid={isPrevPasswordValid}
          validMessage="비밀번호 형식을 올바르게 입력해주세요."
        />
      )}
      <TextInput
        block
        name="login"
        value={email}
        onChange={changeEmail}
        placeholder="계정 이메일"
        validType="email"
        isValid={isEmailValid}
        validMessage="이메일 형식을 올바르게 입력해주세요."
      />
      <PasswordInput
        block
        value={password}
        onChange={changePassword}
        placeholder={addAccount ? '계정 비밀번호' : '비밀번호 입력'}
        onKeyPress={(e) => handleEnterKeyPress(e, handleLogin)}
        isValid={isPasswordValid}
        validMessage="비밀번호 형식을 올바르게 입력해주세요."
      />
      <BtnWrap>
        {handleGoBack && !loginSuccess && (
          <Button onClick={handleGoBack} shape="stroke">
            닫기
          </Button>
        )}
        <Button
          onClick={handleLogin}
          pending={isLoading || loginSuccess}
          disabled={btnDisabled || loginSuccess}
          block={loginSuccess}
        >
          {loginSuccess ? '진입 중...' : loginText || '로그인'}
        </Button>
      </BtnWrap>
      {!loginSuccess && (
        <BtnWrap>
          <Button
            shape="ghost"
            size="sm"
            onClick={() => {
              if (setTitle) {
                handleGoBack();
                history(RootPath.ResetPassword);
              } else {
                setOpenResetPassword(true);
              }
            }}
          >
            비밀번호 찾기
          </Button>
          {!homeSet?.userResiter ? (
            toggleLogin && (
              <Button onClick={toggleLogin} shape="ghost" size="sm">
                회원가입
              </Button>
            )
          ) : (
            <Info>회원가입 기간이 아닙니다.</Info>
          )}
        </BtnWrap>
      )}
      {openResetPassword && (
        <ResetPasswordModal close={closeResetPasswordModal} />
      )}
      {ErrorTypeMap[errorType]}
    </Wrap>
  );
}

export default Login;
